const ESCAPE_PREFIXES = [
  'print.', //
  'ticket.',
  'games_',
  'general.',
  'general_',
  'main_',
  'name_',
];

// Ensure longer prefixes are matched first
ESCAPE_PREFIXES.sort((a, b) => b.length - a.length);

/**
 * Factory: creates a Proxy around translations with prefix/fallback handling
 */
function createTranslationsProxy(translations, additionalPrefixes = []) {
  const prefixes = [...ESCAPE_PREFIXES, ...additionalPrefixes];
  prefixes.sort((a, b) => b.length - a.length);

  // Convert any style (PascalCase, camelCase, kebab-case, etc.) to snake_case
  // Dot-aware: converts only the last segment after '.' to snake_case
  function toSnakeCase(key) {
    if (!key || typeof key !== 'string') return key;

    const parts = key.split('.');
    const last = parts.pop();

    const lastSnake = String(last)
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .replace(/__+/g, '_')
      .toLowerCase();

    return parts.length ? `${parts.join('.')}.${lastSnake}` : lastSnake;
  }

  // Strip known prefix from the beginning of the key
  function stripPrefix(key) {
    const prefix = prefixes.find((p) => key.startsWith(p));
    return prefix ? key.slice(prefix.length) : null;
  }

  // Resolve translation with normalization and iterative prefix stripping
  function getWithFallback(target, key) {
    let current = toSnakeCase(key);

    if (current in target) return target[current];

    let stripped = stripPrefix(current);

    while (stripped && stripped !== current) {
      current = stripped;
      if (current in target) return target[current];
      stripped = stripPrefix(current) ?? stripped;
    }

    return key;
  }

  function hasWithFallback(target, prop) {
    let current = toSnakeCase(prop);
    if (current in target) return true;

    let stripped = stripPrefix(current);

    while (stripped && stripped !== current) {
      current = stripped;
      if (current in target) return true;
      stripped = stripPrefix(current);
    }

    return false;
  }

  return new Proxy(translations, {
    get(target, key, receiver) {
      if (typeof key !== 'string' || ['undefined', 'null', 'value', 'then'].includes(key) || key.startsWith('__v_')) {
        return Reflect.get(target, key, receiver);
      }
      return getWithFallback(target, key);
    },
    has(target, prop) {
      if (typeof prop !== 'string') return Reflect.has(target, prop);
      return hasWithFallback(target, prop);
    },
  });
}

export default createTranslationsProxy;
